'use client';

import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { SvgXMark } from '../Icons/XMark';
import { useDialog } from './useDialog';

export type DrawerWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface DrawerProps {
	open: boolean;
	onClose: () => void;
	children: ReactNode;
	title?: ReactNode;
	width?: DrawerWidth;
	className?: string;
	hideBackdrop?: boolean;
	closeOnBackdropClick?: boolean;
	showCloseIcon?: boolean;
	height?: string;
}

const getDrawerWidth = (width: DrawerWidth = 'md'): string => {
	const widths = {
		sm: 'max-w-sm',
		md: 'max-w-md',
		lg: 'max-w-lg',
		xl: 'max-w-xl',
		'2xl': 'max-w-2xl',
	};
	return widths[width];
};

export const Drawer = ({
	open,
	onClose,
	children,
	title,
	width = '2xl',
	className = '',
	hideBackdrop = false,
	closeOnBackdropClick = true,
	showCloseIcon = true,
	height = 'max-h-[700px]',
}: DrawerProps) => {
	const drawerRef = useDialog(open, onClose);

	const handleBackdropClick = (event: React.MouseEvent) => {
		if (closeOnBackdropClick && event.target === event.currentTarget) {
			onClose();
		}
	};

	if (!open) return null;

	return (
		<div className={twMerge('absolute inset-0 z-15 fade-in')}>
			{/* Backdrop */}
			{!hideBackdrop && (
				<div
					className="absolute inset-0 bg-[var(--color-base-black)]/50 transition-opacity"
					onClick={handleBackdropClick}
					onKeyDown={() => {}}
					aria-hidden="true"
				/>
			)}

			{/* Drawer */}
			<div
				ref={drawerRef}
				aria-modal="true"
				className={twMerge(
					'absolute left-0 right-0 bottom-0 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out rounded-t-xl',
					open ? 'translate-y-0' : 'translate-y-full',
					getDrawerWidth(width),
					'mx-auto',
					className,
				)}
			>
				<div className="px-5 flex flex-col gap-2">
					{/* Header */}
					<div className="flex items-center justify-between px-4 pt-6 pb-2">
						{/* Title Section */}
						{title}
						{/* Close Button Section */}
						{showCloseIcon && (
							<button
								onClick={onClose}
								className="text-[var(--color-base-black)] hover:text-gray-700 cursor-pointer hover:bg-gray-100 rounded-full p-1"
								aria-label="Close drawer"
								type="button"
							>
								<SvgXMark />
							</button>
						)}
					</div>

					{/* Content */}
					<div className={`${height} overflow-y-auto`}>{children}</div>
				</div>
			</div>
		</div>
	);
};
